import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useBeta } from '@/contexts/BetaContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Mail, Lock, User, Phone, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Checkbox } from '@/components/ui/checkbox';
import PlatformDisclaimer from '@/components/PlatformDisclaimer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const { t, language } = useLanguage();
  const { login, register } = useAuth();
  const { betaMode } = useBeta();
  const navigate = useNavigate();
  const [mode, setMode] = useState(() => {
    if (searchParams.get('mode') === 'register') return 'register';
    if (searchParams.get('mode') === 'login') return 'login';
    // If user lands with an invite code, default to register
    if (searchParams.get('invite')) return 'register';
    return 'login';
  });
  // Keep mode in sync when the URL changes (e.g. clicking navbar Sign In / Sign Up
  // while already on /auth — same component, just a new query string).
  useEffect(() => {
    const m = searchParams.get('mode');
    if (m === 'register' || m === 'login') {
      setMode(m);
    } else if (searchParams.get('invite')) {
      setMode('register');
    } else {
      // No mode/invite param → default to login (matches the navbar Sign In link)
      setMode('login');
    }
  }, [searchParams]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '', phone: '', role: 'user' });
  const [inviteCode, setInviteCode] = useState((searchParams.get('invite') || '').toUpperCase());
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSending, setForgotSending] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [agreementVersion, setAgreementVersion] = useState('');
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const tt = (en, es) => (language === 'es' ? es : en);

  // Fetch current Service Agreement version so we can record consent on signup
  useEffect(() => {
    axios.get(`${API}/service-agreement/current`)
      .then(r => setAgreementVersion(r.data?.version || ''))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'login') {
        const data = await login(form.email, form.password);
        toast.success(t('auth.welcomeBack'));
        const role = data.user?.role;
        navigate(role === 'agent' ? '/agency' : role === 'lawyer' ? '/lawyer/dashboard' : (role === 'admin' || role === 'superadmin') ? '/admin' : '/dashboard');
      } else {
        if (!agreementAccepted) {
          toast.error(tt('You must accept the Service Agreement', 'Debes aceptar el Acuerdo de Servicio'));
          setLoading(false);
          return;
        }
        const data = await register(form.email, form.password, form.name, form.phone, form.role, inviteCode, agreementVersion);
        toast.success(t('auth.createAccount'));
        const role = data.user?.role;
        navigate(role === 'agent' ? '/agency' : role === 'lawyer' ? '/lawyer/dashboard' : (role === 'admin' || role === 'superadmin') ? '/admin' : '/dashboard');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally { setLoading(false); }
  };

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: typeof e === 'string' ? e : e.target.value }));

  const onForgotSubmit = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return;
    setForgotSending(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail.trim(), language });
      setForgotSent(true);
    } catch {
      // We never expose errors — the endpoint is intentionally silent.
      setForgotSent(true);
    } finally { setForgotSending(false); }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-[#0B192C] px-4 py-12" data-testid="auth-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl font-bold"><span className="text-[#D4AF37]">Waka</span><span className="text-white">Casa</span></h1>
          <p className="text-sm text-white/50 mt-2">{mode === 'login' ? t('auth.loginSubtitle') : t('auth.registerSubtitle')}</p>
        </div>

        {/* Form */}
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6 shadow-xl">
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <>
                <div>
                  <Label className="text-xs font-medium text-slate-500">{t('auth.name')}</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input placeholder={t('auth.name')} value={form.name} onChange={set('name')} required
                      className="pl-9 h-11 bg-slate-50" data-testid="auth-name" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-500">{t('auth.phone')}</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input placeholder={t('auth.phone')} value={form.phone} onChange={set('phone')}
                      className="pl-9 h-11 bg-slate-50" data-testid="auth-phone" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-500">{t('auth.role')}</Label>
                  <Select value={form.role} onValueChange={set('role')}>
                    <SelectTrigger className="h-11 bg-slate-50 mt-1" data-testid="auth-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">{t('auth.user')}</SelectItem>
                      <SelectItem value="agent">{t('auth.agentRole')}</SelectItem>
                      <SelectItem value="lawyer">{language === 'es' ? 'Abogado' : 'Lawyer'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            {mode === 'register' && betaMode && (
              <div data-testid="invite-code-field">
                <Label className="text-xs font-medium text-slate-500">
                  {language === 'es' ? 'Código de invitación' : 'Invite code'}
                  <span className="text-red-500 ml-0.5">*</span>
                </Label>
                <Input
                  value={inviteCode}
                  onChange={e => setInviteCode(e.target.value.toUpperCase())}
                  placeholder="ABCD1234"
                  required
                  className="mt-1 h-11 bg-slate-50 font-mono tracking-wider uppercase"
                  data-testid="auth-invite-code"
                />
                <p className="text-[10px] text-slate-400 mt-1">
                  {language === 'es' ? 'WakaCasa está en beta. Solicita un código a un administrador.' : 'WakaCasa is in private beta. Ask an admin for a code.'}
                </p>
              </div>
            )}
            <div>
              <Label className="text-xs font-medium text-slate-500">{t('auth.email')}</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input type="email" placeholder={t('auth.email')} value={form.email} onChange={set('email')} required
                  className="pl-9 h-11 bg-slate-50" data-testid="auth-email" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium text-slate-500">{t('auth.password')}</Label>
                {mode === 'login' && (
                  <button type="button"
                    onClick={() => { setForgotOpen(true); setForgotSent(false); setForgotEmail(form.email); }}
                    className="text-[11px] text-[#D4AF37] hover:underline"
                    data-testid="auth-forgot-link">
                    {tt('Forgot password?', '¿Olvidaste tu contraseña?')}
                  </button>
                )}
              </div>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input type="password" placeholder={t('auth.password')} value={form.password} onChange={set('password')} required
                  className="pl-9 h-11 bg-slate-50" data-testid="auth-password" />
              </div>
            </div>
            {mode === 'register' && (
              <div className="flex items-start gap-2" data-testid="auth-agreement-field">
                <Checkbox
                  id="auth-agreement-cb"
                  checked={agreementAccepted}
                  onCheckedChange={(v) => setAgreementAccepted(!!v)}
                  className="mt-0.5 border-slate-300 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                  data-testid="auth-agreement-checkbox"
                />
                <label htmlFor="auth-agreement-cb" className="text-[11px] text-slate-300 leading-snug cursor-pointer">
                  {tt('I have read and accept the', 'He leído y acepto el')}{' '}
                  <Link
                    to="/service-agreement"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#D4AF37] hover:underline"
                    data-testid="auth-agreement-link"
                  >
                    {tt('WakaCasa Service Agreement', 'Acuerdo de Servicio WakaCasa')}
                  </Link>
                  {agreementVersion ? ` (v${agreementVersion})` : ''}.
                </label>
              </div>
            )}
            {mode === 'register' && (
              <PlatformDisclaimer className="text-slate-400" />
            )}
            <Button type="submit" disabled={loading} className="w-full h-11 bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] rounded-xl font-semibold" data-testid="auth-submit-btn">
              {loading ? t('common.loading') : (mode === 'login' ? t('auth.loginBtn') : t('auth.registerBtn'))}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <button type="button" onClick={() => setMode(m => m === 'login' ? 'register' : 'login')}
              className="text-sm text-[#D4AF37] hover:underline" data-testid="auth-toggle-mode">
              {mode === 'login' ? t('auth.noAccount') : t('auth.hasAccount')}
            </button>
          </div>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotOpen} onOpenChange={(v) => { setForgotOpen(v); if (!v) { setForgotSent(false); } }}>
        <DialogContent className="sm:max-w-md" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {tt('Reset your password', 'Restablece tu contraseña')}
            </DialogTitle>
            <DialogDescription>
              {forgotSent
                ? tt('If this email is registered with WakaCasa, a reset link has been sent. Check your inbox in the next few minutes.',
                     'Si el correo está registrado, te hemos enviado un enlace para restablecerla. Revisa tu bandeja de entrada en los próximos minutos.')
                : tt('Enter your account email and we\'ll send you a secure link to set a new password.',
                     'Ingresa el correo de tu cuenta y te enviaremos un enlace seguro para crear una nueva contraseña.')}
            </DialogDescription>
          </DialogHeader>
          {!forgotSent ? (
            <form onSubmit={onForgotSubmit} className="space-y-4 mt-2">
              <div>
                <Label className="text-xs font-medium text-slate-500">{t('auth.email')}</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input type="email" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="you@example.com" className="pl-9 h-11" data-testid="forgot-email-input" />
                </div>
              </div>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button type="button" variant="ghost" onClick={() => setForgotOpen(false)} data-testid="forgot-cancel-btn">
                  {tt('Cancel', 'Cancelar')}
                </Button>
                <Button type="submit" disabled={forgotSending}
                  className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] font-semibold gap-1.5"
                  data-testid="forgot-submit-btn">
                  {forgotSending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  {tt('Send reset link', 'Enviar enlace')}
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <DialogFooter className="mt-2">
              <Button onClick={() => setForgotOpen(false)} className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] font-semibold w-full"
                data-testid="forgot-done-btn">
                {tt('Got it', 'Entendido')}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
